import React, { useState, useEffect } from 'react';
import { Bug, Lightbulb, Clock, CheckCircle, XCircle, Play, Trash2, ExternalLink, Globe, HelpCircle, MessageSquareWarning, FlaskConical, Loader2, ClipboardCopy, FileText, RotateCcw, MoreHorizontal } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { format } from 'date-fns';
import { base44 } from '@/api/base44Client';
import FeedbackAttachments from './FeedbackAttachments';
import FeedbackBugDetails from './FeedbackBugDetails';
import FeedbackAIAssistant from './FeedbackAIAssistant';
import RequestInfoDialog from './RequestInfoDialog';
import CaptainBanner from './CaptainBanner';
/* FeedbackCard.jsx v3.1 — restyled 2026-04-06 */

const STATUS_CONFIG = {
  open: { label: 'Open', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: Clock },
  needs_info: { label: 'Needs Info', color: 'bg-orange-500/20 text-orange-400 border-orange-500/30', icon: HelpCircle },
  rejected: { label: 'Rejected', color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: XCircle },
  promoted: { label: 'Promoted to Issue', color: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30', icon: Play },
  in_progress: { label: 'In Progress', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', icon: Play },
  resolved: { label: 'Resolved', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', icon: CheckCircle },
  closed: { label: 'Closed', color: 'bg-slate-500/20 text-slate-400 border-slate-500/30', icon: XCircle }
};

const PRIORITY_CONFIG = {
  low: { label: 'Low', color: 'bg-slate-100 text-slate-700' },
  medium: { label: 'Medium', color: 'bg-blue-100 text-blue-700' },
  high: { label: 'High', color: 'bg-amber-100 text-amber-700' },
  critical: { label: 'Critical', color: 'bg-red-100 text-red-700' }
};

function getNextStep(item, allFeedback, latestRun, pipelineRun) {
  if (item.status === 'rejected') return { text: '↩️ Returned to submitter — waiting for improvement', color: 'text-red-400 bg-red-500/8 border-red-500/15' };
  if (item.status === 'promoted') return { text: '⬆️ Promoted to Issue — tracked in High Council', color: 'text-indigo-400 bg-indigo-500/8 border-indigo-500/15' };
  if (item.status === 'needs_info') return { text: '⏸️ Waiting for submitter to respond', color: 'text-orange-400 bg-orange-500/8 border-orange-500/15' };
  if (item.status === 'resolved') return { text: '✅ Verified and resolved', color: 'text-emerald-400 bg-emerald-500/8 border-emerald-500/15' };
  if (item.status === 'closed') return { text: '🔒 Closed', color: 'text-slate-400 bg-slate-500/8 border-slate-500/15' };

  // Enrichment in progress (only if explicitly triggered — not for legacy items)
  if (item.enrichment_status === 'pending') return { text: '⏳ Enriching — AI triage in progress...', color: 'text-slate-400 bg-slate-500/8 border-slate-500/15' };

  // Open — needs review
  if (item.status === 'open') {
    if (item.possible_duplicate_of) return { text: '⚠️ Possible duplicate of #' + String(item.possible_duplicate_of).slice(-8) + ' — review before proceeding', color: 'text-amber-400 bg-amber-500/8 border-amber-500/15' };
    return { text: '👀 Review AI triage → set priority, accept, reject, or request info', color: 'text-blue-400 bg-blue-500/8 border-blue-500/15' };
  }

  // In progress
  if (item.status === 'in_progress') {
    /* Pipeline stage takes priority when a PipelineRun exists */
    if (pipelineRun && pipelineRun.stage !== 'resolved' && pipelineRun.stage !== 'cancelled') {
      var stages = {
        briefing: { text: '📋 Generating brief...', color: 'text-amber-400 bg-amber-500/8 border-amber-500/15' },
        reading: { text: '📖 Reading source files...', color: 'text-blue-400 bg-blue-500/8 border-blue-500/15' },
        fixing: { text: '🔧 Fix in progress in claude.ai', color: 'text-violet-400 bg-violet-500/8 border-violet-500/15' },
        testing_pre: { text: '🧪 Pre-deploy testing (API layer)', color: 'text-teal-400 bg-teal-500/8 border-teal-500/15' },
        awaiting_approval: { text: '⏳ Brief ready — paste /pipeline fix command in claude.ai', color: 'text-amber-300 bg-amber-500/10 border-amber-500/20' },
        pushing: { text: '🚀 Pushing to GitHub...', color: 'text-blue-400 bg-blue-500/8 border-blue-500/15' },
        syncing: { text: '⏳ Deploying — waiting for Base44 sync...', color: 'text-blue-400 bg-blue-500/8 border-blue-500/15' },
        testing_post: { text: '🧪 Post-deploy verification (API + browser)', color: 'text-teal-400 bg-teal-500/8 border-teal-500/15' },
        failed: { text: '❌ Pipeline failed — check failure context and retry', color: 'text-red-400 bg-red-500/8 border-red-500/15' }
      };
      return stages[pipelineRun.stage] || { text: '🔧 Pipeline active', color: 'text-violet-400 bg-violet-500/8 border-violet-500/15' };
    }
    /* Fallback: no pipeline run */
    if (latestRun) {
      if (latestRun.status === 'pending' || latestRun.status === 'running') return { text: '🧪 Verification running — waiting for results...', color: 'text-cyan-400 bg-cyan-500/8 border-cyan-500/15' };
      if (latestRun.status === 'failed') {
        var cycle = item.fix_cycle_count || 1;
        return { text: '❌ Cycle ' + cycle + ' failed — failure brief ready, iterate the fix', color: 'text-red-400 bg-red-500/8 border-red-500/15' };
      }
    }
    if (item.latest_brief_id) return { text: '📋 Brief ready — copy to Claude Project → fix → deploy', color: 'text-violet-400 bg-violet-500/8 border-violet-500/15' };
    if (item.cluster_id && allFeedback) {
      var peers = allFeedback.filter(function(f) { return f.cluster_id === item.cluster_id && String(f.id) !== String(item.id) && f.status === 'in_progress'; });
      if (peers.length > 0) return { text: '📦 ' + (peers.length + 1) + ' bugs in cluster "' + item.cluster_id + '" — batch brief recommended', color: 'text-violet-400 bg-violet-500/8 border-violet-500/15' };
    }
    if (item.enriched_at) return { text: '📋 No brief yet — click Fix Brief in ⋯ menu to generate', color: 'text-violet-400 bg-violet-500/8 border-violet-500/15' };
    return { text: '📋 No brief yet — enrich first, or generate via ⋯ menu', color: 'text-slate-400 bg-slate-500/8 border-slate-500/15' };
  }

  return null;
}

// ── Guidance config: what each action does + next steps ──
var ACTION_GUIDANCE = {
  accept: {
    title: '🚀 Accept & Start',
    body: 'Moves this item to In Progress. A Fix Brief auto-generates with full context for Claude.',
    steps: ['Status → In Progress', 'Auto-enrichment runs (if needed)', 'Fix Brief generates automatically', 'Brief ready → copy to Claude → get fix']
  },
  reject: {
    title: '↩ Reject & Return',
    body: 'Returns this item to the submitter with an explanation of what needs improvement.',
    steps: ['You write a rejection message (AI helps draft it)', 'Status → Rejected', 'Submitter gets notified', 'They can improve and resubmit']
  },
  request_info: {
    title: '💬 Request Info',
    body: 'Asks the submitter for more detail before you can act on this item.',
    steps: ['You write a message (AI helps draft it)', 'Status → Needs Info', 'Submitter gets notified', 'When they respond, item returns to your queue']
  },
  enrich: {
    title: '🔬 Enrich',
    body: 'Runs AI analysis to cluster this item, find related bugs, assess complexity, and suggest priority.',
    steps: ['AI scans all feedback for patterns', 'Assigns cluster, complexity, tags', 'Flags possible duplicates', 'Suggests priority level']
  },
  view_brief: {
    title: '📋 View Brief',
    body: 'Opens the generated context document in BriefFixer — ready to copy and paste into Claude.',
    steps: ['Copy the brief text to clipboard', 'Open your Claude Project (link is in the brief)', 'Paste the brief → Claude generates the fix', 'Review code → deploy to Base44 → come back and verify']
  },
  verify: {
    title: '✅ Deployed → Verify',
    body: 'Click AFTER you\'ve deployed the fix code. Creates tests and runs automatic verification.',
    steps: ['Target marked as freshly deployed', 'Acceptance test generated (proves this fix works)', 'Regression tests selected (proves nothing else broke)', 'TesterCaster runs automatically', 'Pass → auto-resolved · Fail → failure brief']
  },
  view_failure_brief: {
    title: '📋 View Failure Brief',
    body: 'Opens the auto-generated failure context — shows what went wrong in the last verification.',
    steps: ['Review which tests failed and why', 'Copy failure brief to Claude for an updated fix', 'Deploy the updated fix', 'Click "Deployed → Verify" to try again']
  },
  retry: {
    title: '🔄 Retry Fix',
    body: 'Re-runs verification against the same target. Use after deploying an updated fix.',
    steps: ['Creates a new test run with the same tests', 'TesterCaster re-executes automatically', 'Pass → auto-resolved · Fail → generates new failure brief']
  },
  generate_brief: {
    title: '📋 Generate Brief',
    body: 'Creates a structured context document with bug details, target system info, and test coverage — ready for Claude.',
    steps: ['AI identifies the target system', 'Assembles bug + system context + test coverage', 'Brief appears on the card', 'Copy to Claude Project to get a fix']
  },
  reopen: {
    title: '🔓 Reopen',
    body: 'Moves this item back to In Progress for further work.',
    steps: ['Status → In Progress', 'You can generate a new brief or verify again']
  },
  waiting: {
    title: '⏸ Waiting',
    body: 'This item is waiting for the submitter to provide more information before you can proceed.',
    steps: ['Submitter was notified to respond', 'When they reply, review and decide: accept, reject, or request more']
  },
  verifying: {
    title: '🧪 Verifying',
    body: 'TesterCaster is running tests against the deployed code right now.',
    steps: ['Acceptance tests check if the specific bug is fixed', 'Regression tests check nothing else broke', 'Results arrive automatically', 'Pass → auto-resolved · Fail → failure brief generated']
  }
};

// ── Info button component: ? with hover tooltip (desktop) + tap toggle (mobile) ──
function InfoButton({ guidanceKey, className: btnClassName }) {
  var guide = ACTION_GUIDANCE[guidanceKey];
  var [open, setOpen] = React.useState(false);
  var [popStyle, setPopStyle] = React.useState({});
  var btnRef = React.useRef(null);

  React.useEffect(function() {
    if (!guide) return;
    if (open && btnRef.current) {
      var rect = btnRef.current.getBoundingClientRect();
      var spaceBelow = window.innerHeight - rect.bottom;
      var spaceRight = window.innerWidth - rect.right;
      // Position: fixed to escape overflow containers
      var style = { position: 'fixed', zIndex: 9999, width: 288 };
      // Vertical: prefer below, flip to above if not enough room
      if (spaceBelow >= 250) {
        style.top = rect.bottom + 8;
      } else {
        style.bottom = window.innerHeight - rect.top + 8;
      }
      // Horizontal: prefer right-aligned to button, flip left if clipped
      if (spaceRight >= 300) {
        style.left = rect.right - 288;
      } else {
        style.left = Math.max(8, rect.left - 288 + 28);
      }
      setPopStyle(style);
    }
  }, [open, guide]);

  if (!guide) return null;

  return (
    <div className="relative inline-flex"
      onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button ref={btnRef} type="button" onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        className={'flex items-center justify-center w-7 h-8 rounded-l-none rounded-r-lg border border-l-0 text-[10px] font-bold transition-colors ' + (btnClassName || 'border-slate-600/40 bg-slate-800/40 text-slate-500 hover:text-slate-200 hover:bg-slate-700/60')}>
        ?
      </button>
      {open && (
        <div style={{...popStyle, background:'#2A3048', borderColor:'#3D4568'}}
          className="rounded-xl p-3.5 shadow-xl shadow-black/30 border"
          onClick={(e) => e.stopPropagation()}>
          <div className="text-[11px] font-bold text-white mb-1.5">{guide.title}</div>
          <div className="text-[10.5px] text-slate-300 leading-relaxed mb-2">{guide.body}</div>
          {guide.steps && guide.steps.length > 0 && (
            <div className="space-y-1">
              {guide.steps.map(function(step, i) {
                return <div key={i} className="text-[10px] text-slate-400 flex items-start gap-1.5">
                  <span className="text-sky-400 font-bold flex-shrink-0">{i + 1}.</span>
                  <span>{step}</span>
                </div>;
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Wraps an action button + ? info button as a joined group ──
function ActionGroup({ children, guidanceKey, infoClassName }) {
  return (
    <div className="inline-flex items-stretch [&>button]:rounded-r-none [&>button]:border-r-0">
      {children}
      <InfoButton guidanceKey={guidanceKey} className={infoClassName} />
    </div>
  );
}

export default function FeedbackCard({
  item,
  getOfficerName,
  onUpdateStatus,
  onUpdatePriority,
  onUpdateAll,
  onDelete,
  onRequestInfo,
  onReject,
  onPromote,
  editingNotes,
  setEditingNotes,
  onSaveNotes,
  batchMode,
  batchSelected,
  onBatchToggle,
  onOpenBrief,
  existingBrief,
  onEnriched,
  allFeedback,
  latestRun,
  onRetry,
  onClusterBatchBrief,
  pipelineRun,
  onResumePipeline
}) {
  const StatusIcon = STATUS_CONFIG[item.status]?.icon || Clock;
  const [requestInfoOpen, setRequestInfoOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [promoteCategory, setPromoteCategory] = useState('execution_headwind');
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [overflowOpen, setOverflowOpen] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyCreated, setVerifyCreated] = useState(false);
  const [verifyMessage, setVerifyMessage] = useState(null); // { type: 'info'|'warn'|'error'|'success', text, target_id?, projectUrl?, projectLabel? }
  const [proposals, setProposals] = useState(null); // array of proposed test cases
  const [generatingTests, setGeneratingTests] = useState(false);
  const [seedingTests, setSeedingTests] = useState(false);
  const [approvedIds, setApprovedIds] = useState(new Set());
  const [fixBrief, setFixBrief] = useState(null);
  const [fixBriefLoading, setFixBriefLoading] = useState(false);
  const [fixBriefCopied, setFixBriefCopied] = useState(false);
  const [savingBrief, setSavingBrief] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [retryLoading, setRetryLoading] = useState(false);
  const [stageJourneyOpen, setStageJourneyOpen] = useState(false);
  const [showRelated, setShowRelated] = useState(false);

  /* Stage 3: load ProjectRegistry once for project_id -> label/url lookup. */
  const [projectRegistry, setProjectRegistry] = useState([]);
  useEffect(() => {
    let cancelled = false;
    base44.entities.ProjectRegistry.list()
      .then(list => { if (!cancelled) setProjectRegistry(list || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  const itemProject = (projectRegistry || []).find(p => p.id === item.project_id) || null;
  const projectLabel = itemProject?.label || itemProject?.domain || '';
  const projectUrl = itemProject?.test_url || itemProject?.project_url || '';

  const isFeatureType = item.type === 'feature' || item.type === 'improvement';

  // Retry Fix: generate failure brief + create retry run with only failed tests
  const handleRetryFix = async () => {
    if (!latestRun || latestRun.status !== 'failed') return;
    setRetryLoading(true);
    try {
      // Step 1: Generate failure brief
      var briefResult = await base44.functions.invoke('testerCasterSeedTests', {
        action: 'generate_failure_brief',
        run_id: latestRun.run_id
      });
      var briefData = briefResult?.data || briefResult;

      if (briefData.skipped) {
        setVerifyMessage({ type: 'info', text: briefData.reason || 'No failures to retry.' });
        setRetryLoading(false);
        return;
      }

      if (briefData.error) {
        setVerifyMessage({ type: 'error', text: 'Failure brief error: ' + briefData.error });
        setRetryLoading(false);
        return;
      }

      // Step 2: Save failure brief as FixBrief entity
      var failBriefId = 'FB-' + new Date().toISOString().split('T')[0].replace(/-/g, '') + '-' +
        String(Date.now()).slice(-4);
      try {
        await base44.entities.FixBrief.create({
          brief_id: failBriefId,
          brief_text: briefData.brief,
          bug_ids: [String(item.id)],
          source: 'failure',
          strategy_note: 'Attempt ' + briefData.next_attempt + ' — ' + briefData.failed_count + ' failed tests from ' + latestRun.run_id,
          target_names: briefData.domain ? [briefData.domain] : [],
          created_by: 'TesterCaster',
          created_at: new Date().toISOString()
        });
      } catch(e) {}

      // Step 3: Copy failure brief to clipboard
      try { await navigator.clipboard.writeText(briefData.brief); } catch(e) {}

      // Step 4: Create retry run with only failed test IDs
      var retryRunId = 'TR-' + new Date().toISOString().split('T')[0].replace(/-/g, '') + '-' +
        String(Date.now()).slice(-4);

      await base44.entities.TestRun.create({
        run_id: retryRunId,
        bug_id: String(item.id),
        requested_by: item.submitted_by || 'unknown',
        assigned_to: 'TesterCaster',
        status: 'pending',
        fix_description: 'RETRY (attempt ' + briefData.next_attempt + '): ' + (item.title || '') + '\n\nRetrying ' + briefData.failed_count + ' previously-failed tests from ' + latestRun.run_id,
        test_ids: briefData.failed_test_ids,
        acceptance_test_ids: [],
        attempt: briefData.next_attempt,
        parent_run_id: latestRun.run_id,
        run_created_at: new Date().toISOString()
      });

      // Step 5: Show success with project link (safe React — no dangerouslySetInnerHTML)
      setVerifyMessage({
        type: 'success',
        text: 'Failure brief saved & copied! ' + briefData.failed_count + ' tests queued (attempt ' + briefData.next_attempt + '). Paste into Claude → iterate → deploy → Mark Deployed → run retry test.',
        projectUrl: briefData.project_url || null,
        projectLabel: briefData.project_label || 'Project'
      });
      if (onRetry) onRetry();
    } catch (err) {
      setVerifyMessage({ type: 'error', text: 'Retry failed: ' + err.message });
    } finally {
      setRetryLoading(false);
    }
  };

  const handleEnrich = async () => {
    setEnriching(true);
    try {
      var result = await base44.functions.invoke('testerCasterSeedTests', {
        action: 'enrich_feedback',
        bug_id: item.id
      });
      var data = result?.data || result;
      if (data.error) {
        setVerifyMessage({ type: 'error', text: 'Enrichment failed: ' + data.error });
      } else {
        setVerifyMessage({ type: 'success', text: 'Enriched: ' + data.cluster_id + ' · ' + data.complexity + (data.related_ids.length > 0 ? ' · ' + data.related_ids.length + ' related' : '') + (data.possible_duplicate_of ? ' · ⚠️ possible duplicate' : '') });
        setTimeout(function() { setVerifyMessage(null); }, 5000);
        if (onEnriched) onEnriched();
      }
    } catch (err) {
      setVerifyMessage({ type: 'error', text: 'Enrich error: ' + err.message });
    } finally {
      setEnriching(false);
    }
  };

  const handleGenerateBrief = async () => {
    setFixBriefLoading(true);
    setFixBrief(null);
    try {
      var briefAction = isFeatureType ? 'generate_build_brief' : 'generate_fix_brief';
      var result = await base44.functions.invoke('testerCasterSeedTests', {
        action: briefAction,
        bug_id: item.id
      });
      var data = result?.data || result;
      if (data.error) {
        setVerifyMessage({ type: 'error', text: 'Brief failed: ' + data.error });
      } else if (data.brief) {
        setFixBrief(data.brief);
      }
    } catch (err) {
      setVerifyMessage({ type: 'error', text: 'Brief error: ' + err.message });
    } finally {
      setFixBriefLoading(false);
    }
  };

  const handleCopyBrief = () => {
    if (!fixBrief) return;
    navigator.clipboard.writeText(fixBrief).then(function() {
      setFixBriefCopied(true);
      setTimeout(function() { setFixBriefCopied(false); }, 2000);
    });
  };

  const handleOpenBriefAsPage = async () => {
    if (!fixBrief) return;
    setSavingBrief(true);
    try {
      var briefId = 'BR-' + new Date().toISOString().split('T')[0].replace(/-/g, '') + '-' + String(Date.now()).slice(-4);
      await base44.entities.FixBrief.create({
        brief_id: briefId,
        brief_text: fixBrief,
        bug_ids: [String(item.id)],
        strategy_note: '',
        source: 'single',
        target_names: [],
        created_by: item.submitted_by || 'unknown',
        created_at: new Date().toISOString()
      });
      if (onOpenBrief) onOpenBrief(briefId);
    } catch (err) {
      console.error('Failed to save brief:', err);
      setVerifyMessage({ type: 'error', text: 'Failed to save brief: ' + err.message });
    } finally {
      setSavingBrief(false);
    }
  };

  const handleGenerateTests = async (targetId) => {
    setGeneratingTests(true);
    try {
      var result = await base44.functions.invoke('testerCasterSeedTests', {
        action: 'generate_tests',
        target: targetId,
        bug_description: (item.admin_notes || item.description || item.title)
      });
      var data = result?.data || result;
      if (data.error) {
        setVerifyMessage({ type: 'error', text: 'Generation failed: ' + data.error });
      } else if (data.proposals && data.proposals.length > 0) {
        setProposals(data.proposals);
        setApprovedIds(new Set(data.proposals.map(function(p) { return p.test_id; }))); // default all approved
        setVerifyMessage({ type: 'success', text: data.count + ' test cases proposed for ' + data.target_name + '.' + (data.lint_fix_count > 0 ? ' (' + data.lint_fix_count + ' auto-fixes applied)' : '') + ' Review below.' });
      } else {
        setVerifyMessage({ type: 'warn', text: 'LLM generated no proposals. Try providing more context in admin notes.' });
      }
    } catch (err) {
      setVerifyMessage({ type: 'error', text: 'Generation error: ' + err.message });
    } finally {
      setGeneratingTests(false);
    }
  };

  const handleSeedApproved = async () => {
    if (!proposals || approvedIds.size === 0) return;
    setSeedingTests(true);
    try {
      var toSeed = proposals.filter(function(p) { return approvedIds.has(p.test_id); });
      var result = await base44.functions.invoke('testerCasterSeedTests', {
        action: 'seed_proposals',
        proposals: toSeed
      });
      var data = result?.data || result;
      setVerifyMessage({ type: 'success', text: data.seeded + ' tests created! You can now click Verify Fix again.' });
      setProposals(null);
      setApprovedIds(new Set());
    } catch (err) {
      setVerifyMessage({ type: 'error', text: 'Seeding error: ' + err.message });
    } finally {
      setSeedingTests(false);
    }
  };

  const toggleProposalApproval = (testId) => {
    setApprovedIds(prev => {
      var next = new Set(prev);
      if (next.has(testId)) next.delete(testId);
      else next.add(testId);
      return next;
    });
  };

  const handleVerifyFix = async () => {
    setVerifyLoading(true);
    setVerifyMessage(null);
    try {
      var fixDesc = item.admin_notes || item.description || item.title;

      // Step 1: Infer target from bug description
      var inferResult = await base44.functions.invoke('testerCasterSeedTests', {
        action: 'infer_target',
        bug_title: item.title,
        bug_description: fixDesc
      });
      var infer = inferResult?.data || inferResult;

      if (!infer.target_id || infer.target_id === 'unknown') {
        setVerifyMessage({ type: 'warn', text: 'Could not identify which system this bug affects. File tests manually in the TesterCaster tab.' });
        setVerifyLoading(false);
        return;
      }

      // Step 1b: Check if bug is frontend-only (not API-testable)
      if (infer.api_testable === false) {
        setVerifyMessage({ type: 'info', text: 'This is a frontend/UI bug (' + infer.target_name + '). API tests cannot verify it — this needs a Browser test or a code fix. Reason: ' + (infer.reasoning || '') });
        setVerifyLoading(false);
        return;
      }

      // Step 2: Generate test(s) — acceptance test for bugs, feature tests for features
      var acceptanceTestIds = [];
      var testsToSeed = [];
      try {
        if (isFeatureType) {
          // Feature: generate feature tests (may return multiple)
          var ftResult = await base44.functions.invoke('testerCasterSeedTests', {
            action: 'generate_feature_test',
            bug_id: item.id,
            target_id: infer.target_id
          });
          var ftData = ftResult?.data || ftResult;
          if (!ftData.skipped && ftData.proposals && ftData.proposals.length > 0) {
            testsToSeed = ftData.proposals;
            acceptanceTestIds = ftData.proposals.map(function(p) { return p.test_id; });
          }
        } else {
          // Bug: generate single acceptance test
          var acceptResult = await base44.functions.invoke('testerCasterSeedTests', {
            action: 'generate_acceptance_test',
            bug_id: item.id,
            target_id: infer.target_id
          });
          var accept = acceptResult?.data || acceptResult;
          if (!accept.skipped && accept.acceptance_test) {
            testsToSeed = [accept.acceptance_test];
            acceptanceTestIds = [accept.acceptance_test.test_id];
          }
        }
      } catch(e) {
        console.warn('Test generation failed:', e.message);
      }

      // Step 3: Select regression tests (may be empty for zero-coverage targets)
      var regressionIds = [];
      if (infer.has_library && infer.test_count > 0) {
        try {
          var selectResult = await base44.functions.invoke('testerCasterSeedTests', {
            action: 'select_tests',
            fix_description: fixDesc,
            bug_title: item.title,
            target: infer.target_id
          });
          var selection = selectResult?.data || selectResult;
          regressionIds = (selection.test_ids || []);
        } catch(e) {
          console.warn('Regression test selection failed:', e.message);
        }
      }

      // Step 4: Combine — we need at least one test to proceed
      var allTestIds = acceptanceTestIds.concat(regressionIds);

      if (allTestIds.length === 0 && testsToSeed.length === 0) {
        var itemLabel = isFeatureType ? 'feature' : 'bug';
        setVerifyMessage({ type: 'info', text: 'No test library exists for "' + infer.target_name + '" and the ' + itemLabel + ' lacked enough detail for test generation.', target_id: infer.target_id });
        setVerifyLoading(false);
        return;
      }

      // Step 5: Seed generated tests into TestCase entity
      if (testsToSeed.length > 0) {
        var seededIds = [];
        for (var si = 0; si < testsToSeed.length; si++) {
          try {
            var prepared = Object.assign({}, testsToSeed[si]);
            if (prepared.input_sequence && typeof prepared.input_sequence !== 'string') {
              prepared.input_sequence = JSON.stringify(prepared.input_sequence);
            }
            if (prepared.assertions && typeof prepared.assertions !== 'string') {
              prepared.assertions = JSON.stringify(prepared.assertions);
            }
            await base44.entities.TestCase.create(prepared);
            seededIds.push(prepared.test_id);
          } catch(e) {
            console.warn('Failed to seed test ' + (testsToSeed[si].test_id || si) + ':', e.message);
          }
        }
        // Update IDs to only include successfully seeded ones
        acceptanceTestIds = seededIds;
        allTestIds = acceptanceTestIds.concat(regressionIds);
        if (allTestIds.length === 0) {
          setVerifyMessage({ type: 'error', text: 'Failed to create tests and no regression tests available.' });
          setVerifyLoading(false);
          return;
        }
      }

      // Step 6: Create TestRun
      var newRunId = 'TR-' + new Date().toISOString().split('T')[0].replace(/-/g, '') + '-' +
        String(Date.now()).slice(-4);

      var typeLabel = isFeatureType ? 'FEATURE' : 'BUG';
      await base44.entities.TestRun.create({
        run_id: newRunId,
        bug_id: String(item.id),
        requested_by: item.submitted_by || 'unknown',
        assigned_to: 'TesterCaster',
        status: 'pending',
        fix_description: typeLabel + ': ' + fixDesc + '\n\n[Target: ' + infer.target_name + ' | Confidence: ' + infer.confidence + ' | Acceptance: ' + acceptanceTestIds.length + ' | Regression: ' + regressionIds.length + '/' + (infer.test_count || 0) + ']',
        test_ids: allTestIds,
        acceptance_test_ids: acceptanceTestIds,
        layers: ['API'],
        loop_count: 1,
        max_loops: 3,
        run_created_at: new Date().toISOString()
      });
      setVerifyCreated(true);

      // Build descriptive success message
      var parts = [];
      var testLabel = isFeatureType ? 'feature test' : 'acceptance test';
      if (acceptanceTestIds.length > 0) parts.push(acceptanceTestIds.length + ' ' + testLabel + (acceptanceTestIds.length > 1 ? 's' : ''));
      if (regressionIds.length > 0) parts.push(regressionIds.length + ' regression test' + (regressionIds.length > 1 ? 's' : ''));
      setVerifyMessage({ type: 'success', text: infer.target_name + ': ' + parts.join(' + ') + ' queued' });
      setTimeout(function() { setVerifyCreated(false); setVerifyMessage(null); }, 6000);
    } catch (err) {
      setVerifyMessage({ type: 'error', text: 'Failed: ' + err.message });
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleApplyAISuggestion = async (suggestion) => {
    const updateData = {};
    if (suggestion.priority) updateData.priority = suggestion.priority;
    if (suggestion.tags) updateData.tags = suggestion.tags;
    if (suggestion.ai_summary) updateData.ai_summary = suggestion.ai_summary;
    if (suggestion.ai_duplicate_risk) updateData.ai_duplicate_risk = suggestion.ai_duplicate_risk;
    if (suggestion.status) updateData.status = suggestion.status;
    if (suggestion.admin_notes && !item.admin_notes) updateData.admin_notes = suggestion.admin_notes;
    await onUpdateAll(item.id, updateData);
  };

  return (
    <Card className="bg-slate-900/50 border-slate-800">
      <CardContent className="p-4">
        {/* Captain banner — soft-lock display + claim/release/transfer/override controls */}
        <div className="mb-3">
          <CaptainBanner feedback={item} />
        </div>
        <div className="flex gap-4">
          {/* Batch mode checkbox */}
          {batchMode && (
            <div className="flex items-center self-start pt-3">
              <input data-testid="fb-search-input"
                type="checkbox"
                checked={!!batchSelected}
                onChange={() => onBatchToggle && onBatchToggle(item.id)}
                className="rounded border-slate-600 bg-slate-800 text-violet-500 focus:ring-violet-500/30 w-4 h-4 cursor-pointer"
              />
            </div>
          )}
          {/* Type Icon */}
          <div className={`p-3 rounded-lg shrink-0 self-start ${item.type === 'bug' ? 'bg-red-500/20' : 'bg-emerald-500/20'}`}>
            {item.type === 'bug' ? (
              <Bug className="w-6 h-6 text-red-400" />
            ) : (
              <Lightbulb className="w-6 h-6 text-emerald-400" />
            )}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="font-semibold text-white text-lg">{item.title || '(untitled)'}</h3>
                <span className="text-[10px] font-mono text-slate-600 bg-slate-800/50 px-1.5 py-0.5 rounded">#{String(item.id)}</span>
                <div className="text-xs text-slate-500 mt-1 flex items-center gap-2 flex-wrap relative">
                  <span>Submitted by {getOfficerName(item.submitted_by)} • {format(new Date(item.created_date), 'MMM d, yyyy HH:mm')}</span>
                  {/* ── Stage Journey pill ── */}
                  {(() => {
                    var SJ_ORDER = ['open', 'needs_info', 'in_progress', 'resolved', 'closed'];
                    var SJ_ABBR  = { open: 'O', needs_info: 'N', in_progress: 'I', resolved: 'R', closed: 'C' };
                    var SJ_LABEL = { open: 'Open', needs_info: 'Needs Info', in_progress: 'In Progress', resolved: 'Resolved', closed: 'Closed' };
                    var SJ_PILL  = { open: 'bg-sky-500/10 text-sky-400 border-sky-500/30', needs_info: 'bg-orange-500/10 text-orange-400 border-orange-500/30', in_progress: 'bg-amber-500/10 text-amber-400 border-amber-500/30', resolved: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30', closed: 'bg-slate-500/10 text-slate-400 border-slate-500/30' };
                    var SJ_TEXT  = { open: 'text-sky-400', needs_info: 'text-orange-400', in_progress: 'text-amber-400', resolved: 'text-emerald-400', closed: 'text-slate-400' };
                    var sjTs = (item.stage_timestamps && typeof item.stage_timestamps === 'object') ? item.stage_timestamps : {};
                    var allSjTs = Object.assign({}, sjTs);
                    if (!allSjTs['open']) allSjTs['open'] = item.created_date || null;
                    var sjLog = [];
                    try { sjLog = typeof item.lifecycle_log === 'string' ? JSON.parse(item.lifecycle_log) : (Array.isArray(item.lifecycle_log) ? item.lifecycle_log : []); } catch(e) {}
                    sjLog.forEach(function(evt) {
                      if (evt.event === 'status_changed' && evt.metadata && evt.metadata.to) {
                        var st = evt.metadata.to;
                        if (!allSjTs[st] && evt.timestamp) allSjTs[st] = evt.timestamp;
                      }
                    });
                    if (!allSjTs[item.status]) allSjTs[item.status] = item.updated_date || new Date().toISOString();
                    var sjVisited = SJ_ORDER.filter(function(s) { return !!allSjTs[s]; });
                    if (sjVisited.length === 0) return null;
                    var curAbbr = SJ_ABBR[item.status] || item.status[0].toUpperCase();
                    var curTs = allSjTs[item.status] ? new Date(allSjTs[item.status]).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
                    var curPill = SJ_PILL[item.status] || 'bg-slate-500/10 text-slate-400 border-slate-500/30';
                    return (
                      <span className="relative">
                        <button onClick={(e) => { e.stopPropagation(); setStageJourneyOpen(function(o) { return !o; }); }}
                          className={'inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-mono cursor-pointer hover:opacity-90 ' + curPill}>
                          <span className="font-bold">{curAbbr}</span>
                          <span className="opacity-50">·</span>
                          <span>{curTs}</span>
                          <span className="opacity-40 ml-0.5 text-[8px]">{stageJourneyOpen ? '▴' : '▾'}</span>
                        </button>
                        {stageJourneyOpen && (<>
                          <span className="fixed inset-0 z-40" onClick={(e) => { e.stopPropagation(); setStageJourneyOpen(false); }} />
                          <span className="absolute left-0 top-full mt-1 z-50 flex flex-col bg-slate-900 border border-slate-700 rounded-lg shadow-xl shadow-black/40 min-w-[200px] py-1 overflow-hidden" onClick={(e) => e.stopPropagation()}>
                            {sjVisited.map(function(stage) {
                              var isCur = stage === item.status;
                              return (
                                <span key={stage} className={'flex items-center gap-2 px-3 py-1.5 text-[10px] ' + (isCur ? 'bg-slate-800/60' : '')}>
                                  <span className={'font-bold font-mono w-3 flex-shrink-0 ' + (SJ_TEXT[stage] || 'text-slate-400')}>{SJ_ABBR[stage] || stage[0].toUpperCase()}</span>
                                  <span className={isCur ? 'text-white/80 font-medium' : 'text-slate-500'}>{SJ_LABEL[stage] || stage}</span>
                                  <span className="text-slate-600 ml-auto pl-3 flex-shrink-0">{allSjTs[stage] ? new Date(allSjTs[stage]).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}</span>
                                </span>
                              );
                            })}
                          </span>
                        </>)}
                      </span>
                    );
                  })()}
                </div>
                {projectLabel && (
                  <div className="flex items-center gap-1.5 mt-1">
                    <Globe className="w-3 h-3 text-cyan-400" />
                    {projectUrl ? (
                      <a
                        href={projectUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-cyan-400 hover:text-cyan-300 underline"
                      >
                        {projectLabel}
                      </a>
                    ) : (
                      <span className="text-xs text-cyan-400">{projectLabel}</span>
                    )}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Badge className={PRIORITY_CONFIG[item.priority || 'medium']?.color}>
                  {PRIORITY_CONFIG[item.priority || 'medium']?.label}
                </Badge>
              </div>
            </div>

            {/* Next step indicator */}
            {(() => {
              var step = getNextStep(item, allFeedback, latestRun, pipelineRun);
              if (!step) return null;
              return (
                <div className={`mt-2 px-3 py-1.5 rounded-lg border text-xs font-medium ${step.color}`}>
                  {step.text}
                </div>
              );
            })()}

            {/* Pipeline status */}
            {pipelineRun && (() => {
              var terminalStages = ['resolved', 'failed', 'cancelled'];
              var isTerminal = terminalStages.indexOf(pipelineRun.stage) !== -1;
              var lastUpdate = new Date(pipelineRun.updated_date || pipelineRun.started_at || 0);
              var hoursStale = (Date.now() - lastUpdate.getTime()) / (1000 * 60 * 60);
              var isStale = !isTerminal && hoursStale > 2;
              var staleLabel = isStale ? ' (stalled ' + Math.floor(hoursStale) + 'h ago)' : '';
              var dotColor = pipelineRun.stage === 'resolved' ? '#22c55e' : pipelineRun.stage === 'failed' ? '#ef4444' : pipelineRun.stage === 'cancelled' ? '#64748b' : isStale ? '#f59e0b' : '#818cf8';
              var dotPulse = (pipelineRun.stage === 'awaiting_approval' || isStale) ? 'plPulse 2s infinite' : 'none';
              return (
              <div style={{marginTop:'10px',padding:'12px',borderRadius:'10px',border:'0.5px solid ' + (isStale ? 'rgba(245,158,11,.25)' : 'rgba(99,102,241,.15)'),background:isStale ? 'rgba(245,158,11,.04)' : 'rgba(99,102,241,.04)'}}>
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'6px'}}>
                  <div style={{display:'flex',alignItems:'center',gap:'6px'}}>
                    <div style={{width:'6px',height:'6px',borderRadius:'50%',background:dotColor,animation:dotPulse}} />
                    <span style={{fontSize:'12px',fontWeight:500,color:isStale ? '#fbbf24' : '#a5b4fc'}}>Pipeline {pipelineRun.run_id}</span>
                  </div>
                  <span style={{fontSize:'10px',color:'#475569'}}>Attempt {pipelineRun.attempt || 1}</span>
                </div>
                <style>{`@keyframes plPulse{0%,100%{opacity:1}50%{opacity:.3}}`}</style>
                <div style={{fontSize:'12px',color:isStale ? '#fbbf24' : '#cbd5e1',marginBottom:6}}>
                  {isStale && '⚠️ Pipeline may be stalled — '}
                  {pipelineRun.stage === 'briefing' && 'Generating brief...'}
                  {pipelineRun.stage === 'reading' && 'Reading source files'}
                  {pipelineRun.stage === 'fixing' && 'Fix in progress'}
                  {pipelineRun.stage === 'testing_pre' && 'Pre-deploy testing'}
                  {pipelineRun.stage === 'awaiting_approval' && 'Push ready — waiting for review'}
                  {pipelineRun.stage === 'pushing' && 'Pushing to GitHub'}
                  {pipelineRun.stage === 'syncing' && 'Deploying...'}
                  {pipelineRun.stage === 'testing_post' && 'Post-deploy verification'}
                  {pipelineRun.stage === 'resolved' && '✅ Verified and resolved'}
                  {pipelineRun.stage === 'failed' && '❌ Failed — retry available'}
                  {pipelineRun.stage === 'cancelled' && 'Cancelled'}
                  {staleLabel}
                </div>
                {pipelineRun.commit_sha && (
                  <div style={{fontSize:'10px',color:'#475569',fontFamily:'monospace',marginBottom:4}}>SHA: {pipelineRun.commit_sha.substring(0, 10)}</div>
                )}
                {pipelineRun.failure_context && pipelineRun.stage === 'failed' && (
                  <div style={{fontSize:'10px',color:'#fca5a5',marginBottom:6,lineHeight:'1.4'}}>{pipelineRun.failure_context.substring(0, 200)}</div>
                )}
                {!isTerminal && (
                  <div style={{display:'flex',flexDirection:'column',gap:'6px',marginTop:'6px'}}>
                    {/* Prominent Resume/Retry banner — opens Launchpad modal */}
                    {(isStale || pipelineRun.stage === 'awaiting_approval' || pipelineRun.stage === 'failed') && (
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          var cmd = pipelineRun.stage === 'failed'
                            ? '/pipeline retry ' + pipelineRun.run_id
                            : '/pipeline fix ' + (pipelineRun.feedback_id || item.id);
                          /* Stage 3: use itemProject from state (already resolved via project_id). */
                          var appLabel = projectLabel || 'Compass';
                          var resumeUrl = itemProject?.claude_project_url || itemProject?.project_url || '';
                          if (onResumePipeline) {
                            onResumePipeline({
                              run_id: pipelineRun.run_id,
                              feedback_id: pipelineRun.feedback_id || String(item.id),
                              title: item.title,
                              domain: appLabel,
                              brief_id: pipelineRun.brief_id || '',
                              project_url: resumeUrl,
                              command: cmd
                            });
                          } else {
                            navigator.clipboard.writeText(cmd);
                          }
                        }}
                        style={{
                          width:'100%',padding:'10px 12px',fontSize:'12px',fontWeight:600,
                          borderRadius:'8px',cursor:'pointer',transition:'all .15s',
                          border: isStale ? '1px solid rgba(245,158,11,.35)' : pipelineRun.stage === 'failed' ? '1px solid rgba(239,68,68,.3)' : '1px solid rgba(99,102,241,.3)',
                          background: isStale ? 'rgba(245,158,11,.08)' : pipelineRun.stage === 'failed' ? 'rgba(239,68,68,.08)' : 'rgba(99,102,241,.08)',
                          color: isStale ? '#fbbf24' : pipelineRun.stage === 'failed' ? '#fca5a5' : '#a5b4fc',
                          display:'flex',alignItems:'center',justifyContent:'center',gap:'8px'
                        }}
                      >
                        {isStale ? '⚠️ Pipeline stalled — click to resume' : pipelineRun.stage === 'failed' ? '🔄 Retry in claude.ai' : '📋 Resume in claude.ai'}
                      </button>
                    )}
                    {/* Cancel — always available for non-terminal */}
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (!confirm('Cancel pipeline run ' + pipelineRun.run_id + '?')) return;
                        try {
                          if (item.status === 'resolved' || item.status === 'closed') {
                            // Card already resolved — delete the PipelineRun entirely, no reopen
                            await base44.entities.PipelineRun.delete(pipelineRun.id);
                          } else {
                            await base44.entities.PipelineRun.update(pipelineRun.id, { stage: 'cancelled', completed_at: new Date().toISOString(), failure_context: 'Cancelled by user from COMPASS' });
                            if (confirm('Move feedback back to Open?')) { onUpdateStatus && onUpdateStatus(item, 'open'); }
                          }
                        } catch(err) { alert('Cancel failed: ' + err.message); }
                      }}
                      style={{padding:'4px 10px',fontSize:'10px',fontWeight:500,borderRadius:'6px',border:'0.5px solid rgba(100,116,139,.25)',background:'transparent',color:'#64748b',cursor:'pointer',alignSelf:'flex-end'}}
                    >
                      ✕ Cancel run
                    </button>
                  </div>
                )}
                {(pipelineRun.stage === 'pushing' || pipelineRun.stage === 'syncing' || pipelineRun.stage === 'awaiting_approval') && pipelineRun.commit_sha && (
                  <button onClick={async (e) => { e.stopPropagation(); if (verifyLoading) return; setVerifyLoading(true); setVerifyMessage({ type: 'info', text: 'Resolving test target...' }); try { var testUrl = 'https://thecompass.base44.app'; var appLabel = projectLabel || 'Compass'; if (itemProject && itemProject.test_url) testUrl = itemProject.test_url; setVerifyMessage({ type: 'info', text: 'Running acceptance test...' }); var ar = null; try { ar = await base44.functions.invoke('testerCasterSeedTests', { action: 'run_test', feedback_id: item.id, test_type: 'acceptance', target: 'post_deploy', test_url: testUrl, source_app: appLabel }); } catch(ae) {} var ad = ar ? (ar.data || ar) : null; var ap = ad && (ad.status === 'PASS' || ad.success); var ask = !ad || ad.error === 'no_acceptance_test'; setVerifyMessage({ type: 'info', text: ask ? 'Running regression...' : (ap ? 'Acceptance PASSED. Regression...' : 'Acceptance FAILED. Regression...') }); var rr2 = await base44.functions.invoke('testerCasterSeedTests', { action: 'run_test', feedback_id: item.id, test_type: 'browser', target: 'post_deploy_regression', test_url: testUrl, source_app: appLabel }); var rd2 = rr2.data || rr2; var rp = rd2.status === 'PASS' || rd2.success; if (ap && rp) setVerifyMessage({ type: 'success', text: '✅ Acceptance + regression PASSED (' + appLabel + ')' }); else if (ask && rp) setVerifyMessage({ type: 'success', text: '✅ Regression PASSED — ' + appLabel }); else { var fp = []; if (!ask && !ap) fp.push('acceptance: ' + (ad.error || 'FAIL')); if (!rp) fp.push('regression: ' + (rd2.error || 'FAIL')); setVerifyMessage({ type: 'error', text: '❌ ' + fp.join(' | ') }); } } catch (err) { setVerifyMessage({ type: 'error', text: 'Error: ' + err.message }); } finally { setVerifyLoading(false); } }}
                    style={{marginTop:'6px',width:'100%',padding:'6px',fontSize:'11px',fontWeight:600,borderRadius:'6px',border:'0.5px solid rgba(34,197,94,.3)',background:verifyLoading ? 'rgba(34,197,94,.05)' : 'rgba(34,197,94,.1)',color:'#6ee7b7',cursor:verifyLoading ? 'wait' : 'pointer',transition:'all .15s',display:'flex',alignItems:'center',justifyContent:'center',gap:'6px'}}>
                    {verifyLoading ? '⏳ Running...' : '▶ Run post-deploy verification'}
                  </button>
                )}
                {verifyMessage && !isTerminal && (
                  <div style={{marginTop:'6px',fontSize:'11px',padding:'6px 8px',borderRadius:'6px', background: verifyMessage.type === 'success' ? 'rgba(34,197,94,.08)' : verifyMessage.type === 'error' ? 'rgba(239,68,68,.08)' : 'rgba(99,102,241,.08)', color: verifyMessage.type === 'success' ? '#6ee7b7' : verifyMessage.type === 'error' ? '#fca5a5' : '#a5b4fc', border: '0.5px solid ' + (verifyMessage.type === 'success' ? 'rgba(34,197,94,.2)' : verifyMessage.type === 'error' ? 'rgba(239,68,68,.2)' : 'rgba(99,102,241,.2)')}}>
                    {verifyMessage.text}
                  </div>
                )}
              </div>
              );
            })()}


            {/* Tags */}
            {item.tags?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1.5">
                {item.tags.map((tag, i) => (
                  <Badge key={i} className="bg-slate-700 text-slate-300 text-[10px] px-1.5 py-0">{tag}</Badge>
                ))}
              </div>
            )}

            {/* Enrichment badges */}
            {item.enriched_at && (
              <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                {item.cluster_id && (() => {
                  var clusterCount = allFeedback ? allFeedback.filter(function(f) { return f.cluster_id === item.cluster_id; }).length : 0;
                  return (
                    <>
                      <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-violet-500/15 text-violet-400 border border-violet-500/25">{item.cluster_id} ({clusterCount})</span>
                      {clusterCount >= 2 && onClusterBatchBrief && (
                        <button
                          onClick={(e) => { e.stopPropagation(); onClusterBatchBrief(item.cluster_id); }}
                          className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-violet-500/10 text-violet-300 border border-violet-500/20 hover:bg-violet-500/25 transition-colors cursor-pointer"
                        >📦 Batch Brief</button>
                      )}
                    </>
                  );
                })()}
                {item.complexity && (
                  <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${
                    item.complexity === 'complex' ? 'bg-red-500/15 text-red-400 border border-red-500/25' :
                    item.complexity === 'moderate' ? 'bg-amber-500/15 text-amber-400 border border-amber-500/25' :
                    'bg-emerald-500/15 text-emerald-400 border border-emerald-500/25'
                  }`}>{item.complexity}</span>
                )}
                {item.possible_duplicate_of && (
                  <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-red-500/15 text-red-400 border border-red-500/25">⚠️ dup of #{item.possible_duplicate_of}</span>
                )}
                {Array.isArray(item.related_ids) && item.related_ids.length > 0 && (
                  <span
                    className="px-1.5 py-0.5 rounded text-[9px] text-cyan-400 bg-cyan-500/10 border border-cyan-500/20 cursor-pointer hover:bg-cyan-500/20 transition-colors"
                    onClick={() => setShowRelated(!showRelated)}
                  >{item.related_ids.length} related {showRelated ? '▾' : '▸'}</span>
                )}
              </div>
            )}
            {/* Related items expanded */}
            {showRelated && Array.isArray(item.related_ids) && item.related_ids.length > 0 && (
              <div className="mt-1.5 ml-0.5 space-y-1 border-l-2 border-cyan-500/20 pl-2.5">
                {item.related_ids.map(function(relId) {
                  var relItem = allFeedback ? allFeedback.find(function(f) { return String(f.id) === String(relId); }) : null;
                  return (
                    <div key={relId} className="flex items-center gap-2 text-[11px]">
                      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${relItem && relItem.type === 'bug' ? 'bg-red-400' : 'bg-emerald-400'}`} />
                      <span className="font-mono text-slate-600 text-[9px]">#{String(relId).slice(-8)}</span>
                      <span className="text-slate-400 truncate">{relItem ? relItem.title : 'Unknown item'}</span>
                      {relItem && <span className={`text-[9px] px-1 rounded ${relItem.status === 'resolved' ? 'text-emerald-500' : relItem.status === 'open' ? 'text-blue-400' : 'text-slate-500'}`}>{relItem.status}</span>}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Auto-triage summary */}
            {item.auto_triage_summary && !item.ai_summary && (
              <p className="text-xs text-indigo-300/70 mt-1.5 italic flex items-center gap-1">
                <span className="text-[9px] px-1 py-0.5 rounded bg-indigo-500/10 text-indigo-400 font-bold flex-shrink-0">AI</span>
                {item.auto_triage_summary}
              </p>
            )}

            {/* AI Summary */}
            {item.ai_summary && (
              <p className="text-xs text-indigo-300/70 mt-1 italic">AI: {item.ai_summary}</p>
            )}

            {/* Link */}
            {item.link && (
              <a
                href={item.link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-indigo-400 hover:text-indigo-300 mt-1"
              >
                <ExternalLink className="w-3 h-3" />
                {item.link}
              </a>
            )}

            {/* Bug-specific details */}
            <FeedbackBugDetails item={item} />

            {/* Feature/improvement description */}
            {item.type !== 'bug' && item.description && (
              <p className="text-slate-300 mt-3 text-sm whitespace-pre-wrap">{item.description}</p>
            )}

            {/* Legacy bug description (for old bug reports before structured fields) */}
            {item.type === 'bug' && item.description && (
              <div className="mt-2">
                <span className="text-slate-500 font-medium text-xs">Additional Details:</span>
                <p className="text-slate-300 text-sm whitespace-pre-wrap mt-0.5">{item.description}</p>
              </div>
            )}

            {/* Attachments */}
            <FeedbackAttachments attachments={item.attachments} />

            {/* AI Assistant */}
            <FeedbackAIAssistant item={item} onApplySuggestion={handleApplyAISuggestion} />

            {/* Admin Controls — Context-Sensitive */}
            <div className="mt-4 pt-4 border-t border-slate-800 space-y-3">

              {/* ── PRIMARY ACTION BAR ── */}
              <div className="flex items-center gap-2 flex-wrap">
                {(() => {
                  var s = item.status;
                  var hasBrief = !!(item.latest_brief_id || existingBrief);
                  var isVerifying = latestRun && (latestRun.status === 'pending' || latestRun.status === 'running');
                  var hasFailed = latestRun && latestRun.status === 'failed';
                  var hasPassed = latestRun && latestRun.status === 'passed';

                  // ── OPEN ──
                  if (s === 'open') {
                    if (!item.enriched_at && item.enrichment_status !== 'failed') {
                      return (
                        <ActionGroup guidanceKey="enrich" infoClassName="border-violet-500/30 bg-violet-600/80 text-violet-200 hover:bg-violet-600 hover:text-white">
                          <Button size="sm" className="h-8 text-xs bg-violet-600 hover:bg-violet-700 text-white" onClick={handleEnrich} disabled={enriching}>
                            {enriching ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Enriching...</> : '🔬 Enrich'}
                          </Button>
                        </ActionGroup>
                      );
                    }
                    return (<>
                      <ActionGroup guidanceKey="accept" infoClassName="border-indigo-500/30 bg-indigo-600/80 text-indigo-200 hover:bg-indigo-600 hover:text-white">
                        <Button size="sm" className="h-8 text-xs bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => onUpdateStatus(item, 'in_progress')}>
                          ✓ Accept & Start
                        </Button>
                      </ActionGroup>
                      <ActionGroup guidanceKey="reject" infoClassName="border-red-500/30 bg-red-500/8 text-red-400/60 hover:bg-red-500/15 hover:text-red-300">
                        <Button size="sm" variant="outline" className="h-8 text-xs border-red-500/30 text-red-400 hover:bg-red-500/10" onClick={() => setRejectDialogOpen(true)}>
                          ↩ Reject
                        </Button>
                      </ActionGroup>
                      <ActionGroup guidanceKey="request_info" infoClassName="border-amber-500/30 bg-amber-500/8 text-amber-400/60 hover:bg-amber-500/15 hover:text-amber-300">
                        <Button size="sm" variant="outline" className="h-8 text-xs border-amber-500/30 text-amber-400 hover:bg-amber-500/10" onClick={() => setRequestInfoOpen(true)}>
                          💬 Request Info
                        </Button>
                      </ActionGroup>
                    </>);
                  }

                  // ── IN PROGRESS ──
                  if (s === 'in_progress') {
                    if (isVerifying) {
                      return (
                        <ActionGroup guidanceKey="verifying" infoClassName="border-violet-500/30 bg-violet-500/8 text-violet-400/60 hover:bg-violet-500/15 hover:text-violet-300">
                          <Button size="sm" variant="outline" className="h-8 text-xs border-violet-500/30 text-violet-400" disabled>
                            ⏳ {latestRun.status === 'running' ? 'Running tests...' : 'Queued for verification...'}
                          </Button>
                        </ActionGroup>
                      );
                    }
                    if (hasFailed) {
                      return (<>
                        {hasBrief && (
                          <ActionGroup guidanceKey="view_failure_brief" infoClassName="border-violet-500/30 bg-violet-500/8 text-violet-400/60 hover:bg-violet-500/15 hover:text-violet-300">
                            <Button size="sm" variant="outline" className="h-8 text-xs border-violet-500/30 text-violet-400 hover:bg-violet-500/10"
                              onClick={() => onOpenBrief && onOpenBrief(existingBrief?.brief_id || item.latest_brief_id)}>
                              📋 View Failure Brief
                            </Button>
                          </ActionGroup>
                        )}
                        <ActionGroup guidanceKey="retry" infoClassName="border-red-500/30 bg-red-500/8 text-red-400/60 hover:bg-red-500/15 hover:text-red-300">
                          <Button size="sm" variant="outline" className="h-8 text-xs border-red-500/30 text-red-400 hover:bg-red-500/10"
                            onClick={handleRetryFix} disabled={retryLoading}>
                            {retryLoading ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Retrying...</> : <><RotateCcw className="w-3.5 h-3.5 mr-1" /> Retry Fix</>}
                          </Button>
                        </ActionGroup>
                      </>);
                    }
                    if (hasBrief) {
                      return (<>
                        <ActionGroup guidanceKey="view_brief" infoClassName="border-violet-500/30 bg-violet-500/8 text-violet-400/60 hover:bg-violet-500/15 hover:text-violet-300">
                          <Button size="sm" variant="outline" className="h-8 text-xs border-violet-500/30 text-violet-400 hover:bg-violet-500/10"
                            onClick={() => onOpenBrief && onOpenBrief(existingBrief?.brief_id || item.latest_brief_id)}>
                            📋 View Brief
                          </Button>
                        </ActionGroup>
                        <ActionGroup guidanceKey="verify" infoClassName="border-cyan-500/30 bg-cyan-500/8 text-cyan-400/60 hover:bg-cyan-500/15 hover:text-cyan-300">
                          <Button size="sm" variant="outline" className="h-8 text-xs border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10"
                            onClick={handleVerifyFix} disabled={verifyLoading || verifyCreated}>
                            {verifyLoading ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Analyzing...</>
                              : verifyCreated ? <><CheckCircle className="w-3.5 h-3.5 mr-1" /> Run Created</>
                              : <><FlaskConical className="w-3.5 h-3.5 mr-1" /> {isFeatureType ? 'Verify Feature' : 'Verify Fix'}</>}
                          </Button>
                        </ActionGroup>
                      </>);
                    }
                    // No brief yet — generating or needs manual
                    return (
                      <ActionGroup guidanceKey="generate_brief" infoClassName="border-violet-500/30 bg-violet-500/8 text-violet-400/60 hover:bg-violet-500/15 hover:text-violet-300">
                        <Button size="sm" variant="outline" className="h-8 text-xs border-violet-500/30 text-violet-400" onClick={handleGenerateBrief} disabled={fixBriefLoading}>
                          {fixBriefLoading ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Generating Brief...</> : <><FileText className="w-3.5 h-3.5 mr-1" /> {isFeatureType ? 'Build Brief' : 'Fix Brief'}</>}
                        </Button>
                      </ActionGroup>
                    );
                  }

                  // ── NEEDS INFO ──
                  if (s === 'needs_info') {
                    return (
                      <ActionGroup guidanceKey="waiting" infoClassName="border-amber-500/30 bg-amber-500/8 text-amber-400/60 hover:bg-amber-500/15 hover:text-amber-300">
                        <Button size="sm" variant="outline" className="h-8 text-xs border-amber-500/30 text-amber-400" disabled>
                          ⏸ Waiting for submitter
                        </Button>
                      </ActionGroup>
                    );
                  }

                  // ── REJECTED ──
                  if (s === 'rejected') {
                    return (
                      <ActionGroup guidanceKey="reopen" infoClassName="border-slate-600/40 bg-slate-800/40 text-slate-500 hover:text-slate-300 hover:bg-slate-700/60">
                        <Button size="sm" variant="outline" className="h-8 text-xs border-slate-600 text-slate-400 hover:bg-slate-800" onClick={() => onUpdateStatus(item, 'open')}>
                          🔓 Reopen
                        </Button>
                      </ActionGroup>
                    );
                  }

                  // ── PROMOTED ──
                  if (s === 'promoted') {
                    return (
                      <span className="text-xs text-indigo-400 px-3 py-1.5 rounded-lg bg-indigo-500/8 border border-indigo-500/15">⬆️ Tracked in High Council</span>
                    );
                  }

                  // ── RESOLVED / CLOSED ──
                  if (s === 'resolved' || s === 'closed') {
                    return (<>
                      {hasPassed && <span className="text-xs text-emerald-400 px-2 py-0.5 rounded-full bg-emerald-500/10 font-bold">✓ Verified</span>}
                      <ActionGroup guidanceKey="reopen" infoClassName="border-slate-600/40 bg-slate-800/40 text-slate-500 hover:text-slate-300 hover:bg-slate-700/60">
                        <Button size="sm" variant="outline" className="h-8 text-xs border-slate-600 text-slate-400 hover:bg-slate-800" onClick={() => onUpdateStatus(item, 'in_progress')}>
                          🔓 Reopen
                        </Button>
                      </ActionGroup>
                    </>);
                  }

                  return null;
                })()}

                {/* Run status pill */}
                {latestRun && item.status === 'in_progress' && (
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                    latestRun.status === 'passed' ? 'bg-emerald-500/15 text-emerald-400' :
                    latestRun.status === 'failed' ? 'bg-red-500/15 text-red-400' :
                    'bg-amber-500/15 text-amber-400'
                  }`}>
                    {latestRun.status === 'passed' ? '✓ Passed' : latestRun.status === 'failed' ? '✗ Failed' : '⏳ ' + latestRun.status}
                  </span>
                )}

                {/* Fix cycle count */}
                {(item.fix_cycle_count || 0) > 1 && (
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-500/15 text-amber-400">
                    Cycle {item.fix_cycle_count}
                  </span>
                )}

                {/* ── OVERFLOW MENU ⋯ ── */}
                <div className="relative ml-auto">
                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-slate-500 hover:text-white hover:bg-slate-700"
                    onClick={() => setOverflowOpen(!overflowOpen)}>
                    <MoreHorizontal className="w-4 h-4" />
                  </Button>
                  {overflowOpen && (
                    <>
                      <div data-testid="fb-feedbackcard-clickable-div" className="fixed inset-0 z-40" onClick={() => setOverflowOpen(false)} />
                      <div data-testid="fb-feedbackcard-clickable-div-1" className="absolute right-0 top-full mt-1 z-50 bg-slate-900 border border-slate-700 rounded-xl shadow-xl shadow-black/30 w-56 py-1" onClick={() => setOverflowOpen(false)}>
                        {/* Status */}
                        <div className="px-3 py-2 border-b border-slate-800">
                          <div className="text-[9px] text-slate-500 font-bold uppercase tracking-wider mb-1.5">Status</div>
                          <div className="flex flex-wrap gap-1">
                            {['open','needs_info','in_progress','resolved','closed'].map(function(st) {
                              var labels = { open:'Open', needs_info:'Needs Info', in_progress:'In Progress', resolved:'Resolved', closed:'Closed' };
                              return (
                                <button key={st} onClick={() => onUpdateStatus(item, st)}
                                  className={`px-2 py-0.5 rounded text-[10px] font-semibold transition-colors ${item.status === st ? 'bg-indigo-500/20 text-indigo-400 border border-indigo-500/30' : 'bg-slate-800 text-slate-400 hover:bg-slate-700 border border-transparent'}`}>
                                  {labels[st]}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                        {/* Priority */}
                        <div className="px-3 py-2 border-b border-slate-800">
                          <div className="text-[9px] text-slate-500 font-bold uppercase tracking-wider mb-1.5">Priority</div>
                          <div className="flex gap-1">
                            {['low','medium','high','critical'].map(function(pr) {
                              var colors = { low:'text-slate-400', medium:'text-blue-400', high:'text-amber-400', critical:'text-red-400' };
                              return (
                                <button key={pr} onClick={() => onUpdatePriority(item.id, pr)}
                                  className={`px-2 py-0.5 rounded text-[10px] font-semibold capitalize transition-colors ${(item.priority || 'medium') === pr ? 'bg-indigo-500/20 text-indigo-400 border border-indigo-500/30' : 'bg-slate-800 ' + colors[pr] + ' hover:bg-slate-700 border border-transparent'}`}>
                                  {pr}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                        {/* Action items */}
                        {item.status !== 'closed' && item.status !== 'resolved' && (
                          <button onClick={() => setRequestInfoOpen(true)} className="w-full px-3 py-2 text-left text-xs text-slate-300 hover:bg-slate-800 flex items-center gap-2">
                            <MessageSquareWarning className="w-3.5 h-3.5 text-amber-400" /> Request Info
                          </button>
                        )}
                        {item.status !== 'closed' && item.status !== 'resolved' && (
                          <button onClick={() => setRejectDialogOpen(true)} className="w-full px-3 py-2 text-left text-xs text-slate-300 hover:bg-slate-800 flex items-center gap-2">
                            <XCircle className="w-3.5 h-3.5 text-red-400" /> Reject & Return
                          </button>
                        )}
                        {item.status !== 'promoted' && !item.promoted_to_issue_id && (
                          <button onClick={() => { setOverflowOpen(false); setPromoteOpen(true); }} className="w-full px-3 py-2 text-left text-xs text-slate-300 hover:bg-slate-800 flex items-center gap-2">
                            <ExternalLink className="w-3.5 h-3.5 text-indigo-400" /> Promote to Issue
                          </button>
                        )}
                        {item.status !== 'closed' && (
                          <button onClick={() => { setOverflowOpen(false); handleEnrich(); }} className="w-full px-3 py-2 text-left text-xs text-slate-300 hover:bg-slate-800 flex items-center gap-2">
                            🔬 {item.enriched_at ? 'Re-enrich' : 'Enrich'}
                          </button>
                        )}
                        {item.status !== 'closed' && (
                          <button onClick={() => { setOverflowOpen(false); handleGenerateBrief(); }} className="w-full px-3 py-2 text-left text-xs text-slate-300 hover:bg-slate-800 flex items-center gap-2">
                            <FileText className="w-3.5 h-3.5 text-violet-400" /> {existingBrief || item.latest_brief_id ? 'Regenerate Brief' : (isFeatureType ? 'Build Brief' : 'Fix Brief')}
                          </button>
                        )}
                        {item.status !== 'closed' && !latestRun?.status?.match(/pending|running/) && (
                          <button onClick={() => { setOverflowOpen(false); handleVerifyFix(); }} className="w-full px-3 py-2 text-left text-xs text-slate-300 hover:bg-slate-800 flex items-center gap-2">
                            <FlaskConical className="w-3.5 h-3.5 text-cyan-400" /> {isFeatureType ? 'Verify Feature' : 'Verify Fix'}
                          </button>
                        )}
                        <div className="border-t border-slate-800 mt-1 pt-1">
                          <button onClick={() => { if (confirm('Delete this feedback?')) { onDelete(item.id); } }}
                            className="w-full px-3 py-2 text-left text-xs text-red-400 hover:bg-red-500/10 flex items-center gap-2">
                            <Trash2 className="w-3.5 h-3.5" /> Delete
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Promote popover — triggered from overflow */}
              {promoteOpen && (
                <div style={{position:'relative'}}>
                  <div data-testid="fb-feedbackcard-clickable-div-2" className="fixed inset-0 z-40" onClick={() => setPromoteOpen(false)} />
                  <div data-testid="fb-feedbackcard-clickable-div-3" className="absolute left-0 bottom-full mb-1 z-50 bg-slate-900 border border-slate-700 rounded-xl p-4 w-72 shadow-xl shadow-black/30 space-y-3" onClick={e => e.stopPropagation()}>
                    <h3 className="text-white font-semibold text-xs">Promote to Organizational Issue</h3>
                    <p className="text-[10px] text-slate-400">Creates an Issue from this {item.type === 'bug' ? 'bug report' : 'feature request'} for the High Council queue.</p>
                    <div className="space-y-1">
                      <label className="text-[10px] text-slate-300 font-semibold">Issue Category</label>
                      <select value={promoteCategory} onChange={e => setPromoteCategory(e.target.value)}
                        className="w-full bg-slate-800 border border-slate-700 text-slate-200 rounded-md h-8 px-2 text-xs">
                        <option value="execution_headwind">Execution Headwind</option>
                        <option value="capability_gap">Capability Gap</option>
                        <option value="process_friction">Process Friction</option>
                        <option value="red_line_violation">Red Line Violation</option>
                      </select>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => { onPromote?.(item, promoteCategory); setPromoteOpen(false); }}
                        className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg h-8 text-xs font-semibold transition-colors">
                        Promote
                      </button>
                      <button onClick={() => setPromoteOpen(false)}
                        className="px-3 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg h-8 text-xs transition-colors">
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Cluster batch recommendation */}
              {item.status === 'in_progress' && item.cluster_id && allFeedback && (() => {
                var clusterPeers = allFeedback.filter(f => f.cluster_id === item.cluster_id && String(f.id) !== String(item.id) && f.status === 'in_progress');
                if (clusterPeers.length > 0) return (
                  <div className="px-2.5 py-1.5 rounded bg-violet-500/8 border border-violet-500/20 text-[10px] text-violet-300 flex items-center gap-1.5">
                    <span>📦</span>
                    <span><strong>{clusterPeers.length + 1}</strong> bugs in cluster <strong>{item.cluster_id}</strong> are in-progress — consider a Batch Brief</span>
                  </div>
                );
                return null;
              })()}

              {/* Verify message + Generate Tests + Proposal Review */}
              {verifyMessage && (
                <div className={`px-3 py-2 rounded text-xs ${
                  verifyMessage.type === 'success' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' :
                  verifyMessage.type === 'warn' ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20' :
                  verifyMessage.type === 'info' ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20' :
                  'bg-red-500/10 text-red-400 border border-red-500/20'
                }`}>
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <span>{verifyMessage.text}</span>
                      {verifyMessage.projectUrl && (
                        <a href={verifyMessage.projectUrl} target="_blank" rel="noopener noreferrer"
                          className="ml-2 text-violet-400 underline hover:text-violet-300">
                          Open {verifyMessage.projectLabel || 'Project'}
                        </a>
                      )}
                    </div>
                    {(verifyMessage.type === 'info' || verifyMessage.type === 'warn') && verifyMessage.target_id && !proposals && (
                      <Button
                        size="sm"
                        onClick={() => handleGenerateTests(verifyMessage.target_id)}
                        disabled={generatingTests}
                        className="bg-purple-600 hover:bg-purple-700 text-white text-[11px] h-7 px-3 flex-shrink-0"
                      >
                        {generatingTests
                          ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Generating...</>
                          : <><FlaskConical className="w-3 h-3 mr-1" /> Generate Tests</>
                        }
                      </Button>
                    )}
                  </div>
                </div>
              )}

                {/* ── PROPOSAL REVIEW ── */}
                {proposals && proposals.length > 0 && (
                  <div className="w-full mt-3 border border-purple-500/20 rounded-lg overflow-hidden bg-slate-950/40">
                    <div className="px-3 py-2 bg-purple-500/8 border-b border-purple-500/20 flex items-center justify-between">
                      <span className="text-xs font-bold text-purple-300">Proposed Tests ({approvedIds.size}/{proposals.length} approved)</span>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={handleSeedApproved}
                          disabled={seedingTests || approvedIds.size === 0}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] h-7 px-3"
                        >
                          {seedingTests
                            ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Seeding...</>
                            : <><CheckCircle className="w-3 h-3 mr-1" /> Approve & Seed ({approvedIds.size})</>
                          }
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => { setProposals(null); setApprovedIds(new Set()); }}
                          className="text-slate-500 text-[11px] h-7 px-2 hover:text-white"
                        >
                          Discard All
                        </Button>
                      </div>
                    </div>
                    <div className="divide-y divide-slate-800/50">
                      {proposals.map(function(p) {
                        var isApproved = approvedIds.has(p.test_id);
                        var stepCount = (p.input_sequence || []).length;
                        var assertionCount = (p.input_sequence || []).reduce(function(sum, s) { return sum + (s.assertions || []).length; }, 0);
                        return (
                          <div key={p.test_id} className={`px-3 py-2 ${isApproved ? 'bg-purple-500/5' : 'opacity-40'}`}>
                            <div className="flex items-start gap-2">
                              <input data-testid="fb-feedbackcard-input-1"
                                type="checkbox"
                                checked={isApproved}
                                onChange={() => toggleProposalApproval(p.test_id)}
                                className="mt-0.5 rounded border-slate-600 bg-slate-800 text-purple-500 focus:ring-purple-500/30"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="font-mono text-[11px] font-bold text-cyan-400">{p.test_id}</span>
                                  <span className="text-xs text-slate-300 font-medium">{p.name}</span>
                                  <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${
                                    p.priority === 'critical' ? 'bg-red-500/15 text-red-400' :
                                    p.priority === 'high' ? 'bg-amber-500/15 text-amber-400' :
                                    'bg-slate-500/15 text-slate-400'
                                  }`}>{p.priority}</span>
                                </div>
                                <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-2">{p.description}</p>
                                <div className="flex gap-3 mt-1 text-[10px] text-slate-600">
                                  <span>{stepCount} step{stepCount !== 1 ? 's' : ''}</span>
                                  <span>{assertionCount} assertion{assertionCount !== 1 ? 's' : ''}</span>
                                  <span>target: {p.target}</span>
                                  {p.tags && <span>tags: {(Array.isArray(p.tags) ? p.tags : []).slice(0, 3).join(', ')}</span>}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* ── FIX BRIEF PANEL ── */}
                {fixBrief && (
                  <div className="w-full mt-3 border border-violet-500/20 rounded-lg overflow-hidden bg-slate-950/40">
                    <div className="px-3 py-2 bg-violet-500/8 border-b border-violet-500/20 flex items-center justify-between">
                      <span className="text-xs font-bold text-violet-300">Fix Brief{existingBrief ? ' (updated)' : ' — New'}</span>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={handleCopyBrief}
                          className="bg-violet-600 hover:bg-violet-700 text-white text-[11px] h-7 px-3"
                        >
                          {fixBriefCopied
                            ? <><CheckCircle className="w-3 h-3 mr-1" /> Copied!</>
                            : <><ClipboardCopy className="w-3 h-3 mr-1" /> Copy</>
                          }
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleOpenBriefAsPage}
                          disabled={savingBrief}
                          className="bg-cyan-600 hover:bg-cyan-700 text-white text-[11px] h-7 px-3"
                        >
                          {savingBrief ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Saving...</> : 'Save & View'}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setFixBrief(null)}
                          className="text-slate-500 text-[11px] h-7 px-2 hover:text-white"
                        >
                          Dismiss
                        </Button>
                      </div>
                    </div>
                    <div className="px-3 py-2 max-h-[300px] overflow-y-auto">
                      <pre className="text-[11px] text-slate-400 whitespace-pre-wrap font-mono leading-relaxed">{fixBrief}</pre>
                    </div>
                    <div className="px-3 py-2 bg-violet-500/5 border-t border-violet-500/10">
                      <p className="text-[10px] text-violet-400/60">Copy → open the COMPASS Project link at bottom of brief → paste → get fix → deploy → Verify Fix</p>
                    </div>
                  </div>
                )}

              {/* Admin Notes */}
              <div className="space-y-2">
                <label className="text-xs text-slate-500 font-medium">Admin Notes</label>
                <div className="flex gap-2">
                  <Textarea data-testid="fb-feedbackcard-textarea"
                    value={editingNotes[item.id] !== undefined ? editingNotes[item.id] : (item.admin_notes || '')}
                    onChange={(e) => setEditingNotes(prev => ({ ...prev, [item.id]: e.target.value }))}
                    placeholder="Add notes..."
                    className="bg-slate-800 border-slate-700 text-slate-100 text-sm min-h-[60px]"
                  />
                  {editingNotes[item.id] !== undefined && editingNotes[item.id] !== (item.admin_notes || '') && (
                    <Button
                      size="sm"
                      onClick={() => onSaveNotes(item.id)}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white"
                    >
                      Save
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>



          {/* ── Lifecycle Timeline ── */}
          {(() => {
            var log = [];
            try { log = typeof item.lifecycle_log === 'string' ? JSON.parse(item.lifecycle_log) : (Array.isArray(item.lifecycle_log) ? item.lifecycle_log : []); } catch(e) {}
            if (log.length === 0) return null;

            var EVENT_DISPLAY = {
              submitted: { icon: '📥', color: 'bg-blue-400' },
              enriched: { icon: '🔬', color: 'bg-violet-400' },
              triaged: { icon: '🏷️', color: 'bg-violet-400' },
              status_changed: { icon: '🔄', color: 'bg-amber-400' },
              briefed: { icon: '📋', color: 'bg-violet-400' },
              deployed: { icon: '🚀', color: 'bg-emerald-400' },
              verify_started: { icon: '🧪', color: 'bg-cyan-400' },
              verify_passed: { icon: '✅', color: 'bg-emerald-400' },
              verify_failed: { icon: '❌', color: 'bg-red-400' },
              resolved: { icon: '🎉', color: 'bg-emerald-400' },
              rejected: { icon: '↩️', color: 'bg-red-400' },
              promoted: { icon: '⬆️', color: 'bg-indigo-400' },
              reopened: { icon: '🔓', color: 'bg-amber-400' },
            };

            return (
              <details className="mt-4 pt-3 border-t border-slate-800">
                <summary className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider cursor-pointer hover:text-slate-300 select-none">
                  History ({log.length} events) {item.fix_cycle_count > 0 && <span className="text-amber-400 normal-case">· Cycle {item.fix_cycle_count}</span>}
                </summary>
                <div className="mt-2 space-y-1">
                  {log.slice().reverse().map(function(evt, idx) {
                    var cfg = EVENT_DISPLAY[evt.event] || { icon: '•', color: 'bg-slate-400' };
                    var actor = evt.actor === 'system' ? '🤖 system' : evt.actor === 'testercaster@compass.ai' ? '🧪 TesterCaster' : '👤 ' + (evt.actor || '').split('@')[0];
                    var meta = evt.metadata || {};
                    var detail = '';
                    if (evt.event === 'status_changed') detail = (meta.from || '?') + ' → ' + (meta.to || '?');
                    else if (evt.event === 'briefed') detail = (meta.auto ? 'auto' : 'manual') + ' · ' + (meta.brief_type || 'solo');
                    else if (evt.event === 'deployed') detail = meta.domain || '';
                    else if (evt.event === 'verify_started') detail = meta.test_count + ' tests';
                    else if (evt.event === 'enriched') detail = meta.cluster_id || '';
                    else if (evt.event === 'triaged') detail = meta.suggested_priority || '';
                    else if (meta.run_id) detail = meta.run_id;

                    return (
                      <div key={idx} className="flex items-center gap-2 text-[10px] py-1 px-2 rounded hover:bg-slate-800/50">
                        <span>{cfg.icon}</span>
                        <span className={'w-1.5 h-1.5 rounded-full flex-shrink-0 ' + cfg.color} />
                        <span className="text-slate-300 font-medium w-24 flex-shrink-0">{evt.event}</span>
                        <span className="text-slate-500 flex-shrink-0 w-20">{actor}</span>
                        {detail && <span className="text-slate-500">{detail}</span>}
                        <span className="text-slate-600 ml-auto flex-shrink-0">{evt.timestamp ? new Date(evt.timestamp).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}</span>
                      </div>
                    );
                  })}
                </div>
              </details>
            );
          })()}
        </div>
      </CardContent>

      <RequestInfoDialog
        open={requestInfoOpen}
        onOpenChange={setRequestInfoOpen}
        item={item}
        onSend={(message) => onRequestInfo(item, message)}
      />

      <RequestInfoDialog
        open={rejectDialogOpen}
        onOpenChange={setRejectDialogOpen}
        item={item}
        onSend={(message) => { onReject?.(item, message); }}
        mode="reject"
      />
    </Card>
  );
}


